import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";

// --- Utility Function ---
const formatNumber = (val) => {
  const num = Number(val || 0);
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// --- Stylesheet (ADJUSTED for 100% width across two modes) ---
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    paddingTop: 30,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 30,
    fontSize: 7.5, // Reduced font slightly to fit more columns
  },
  // ... Header Styles ...
  clientHeader: {
    marginBottom: 10,
    borderBottom: "1px solid #333",
    paddingBottom: 5,
  },
  clientName: { fontSize: 12, fontWeight: "bold" },
  reportTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 5,
    marginTop: 5,
    color: "#333",
    justifyContent: "center",
  },

  // --- Table & Row Styles ---
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: { flexDirection: "row" },
  tableHeader: {
    backgroundColor: "#404040",
    color: "white",
    fontWeight: "bold",
    textAlign: "center",
  },
  footerRow: { backgroundColor: "#F0F0F0", fontWeight: "bold" },

  // --- BASE Column Styles (All 100% Total) ---
  colDefault: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 3,
    overflow: "hidden",
  },
  colText: { textAlign: "left" },
  colNum: { textAlign: "right" },
});

// === COLUMN DEFINITIONS FOR DIFFERENT MODES ===

// Mode 1: Invoice wise Profit (13 columns) - Total width 100%
const PURCHASE_SUPPLIER_MAP = [
  { label: "ACCOUNT", key: "ACCOUNT", width: "20%" },
  { label: "AMOUNT", key: "AMOUNT", width: "15%" },
  { label: "EXCL_VAT_AMOUNT", key: "EXCL_VAT_AMOUNT", width: "15%" },
  { label: "SUPPLIER", key: "SUPPLIER", width: "30%" },
  { label: "VATAMT", key: "VATAMT", width: "20%" },
];

const PURCHASELIST_MAP = [
  { label: "DEPT NO", key: "DEPTNO", width: "8%" },
  { label: "INV NO", key: "INV NO", width: "8%" },
  { label: "DATE", key: "DATE", width: "6%" },
  { label: "ACCOUNT", key: "ACCOUNT", width: "10%" },
  { label: "CUSTOMER", key: "CUSTOMER", width: "10%" },
  { label: "SALESMAN", key: "SALES MAN", width: "9%" },
  { label: "GROSS SALE", key: "GROSS SALE", width: "7%", isNumeric: true },
  { label: "DISC", key: "DISC", width: "6%", isNumeric: true },
  { label: "NET SALES", key: "NET SALES", width: "7%", isNumeric: true },
  { label: "COST", key: "COST", width: "7%", isNumeric: true },
  { label: "PROFIT", key: "PROFIT", width: "7%", isNumeric: true },
  { label: "% AGE", key: "PERCENTAGE", width: "5%", isNumeric: true },
  { label: "VAT AMT", key: "VAT AMT", width: "10%", isNumeric: true }, // Extra width here for better fit
]; // Total: 8+8+6+10+10+9+7+6+7+7+7+5+10 = 100%

// Mode 2: Item wise Profit (20+ columns) - Requires very narrow columns
const PURCHASE_LOCAL_MAP = [
  { label: "DEPT NO", key: "DEPTNO", width: "4.5%" },
  { label: "INV NO", key: "INV NO", width: "4.5%" },
  { label: "DATE", key: "DATE", width: "4%" },
  { label: "CODE", key: "CODE", width: "6%" },
  { label: "DESCRIPTION", key: "DESCRIPTION", width: "8%" },
  { label: "QTY", key: "QTY", width: "4%", isNumeric: true },
  { label: "U. PRICE", key: "U_PRICE", width: "5%", isNumeric: true },
  { label: "DISC", key: "DISC", width: "4%", isNumeric: true },
  { label: "U. COST", key: "U_COST", width: "5%", isNumeric: true },
  { label: "AMOUNT", key: "AMOUNT", width: "5%", isNumeric: true },
  { label: "TOTAL COST", key: "TOTAL_COST", width: "5%", isNumeric: true },
  { label: "PROFIT", key: "PROFIT", width: "5%", isNumeric: true },
  { label: "% AGE", key: "PERCENTAGE", width: "3%", isNumeric: true },
  { label: "SALESMAN", key: "SALESMAN", width: "5.5%" },
  { label: "CUSTOMER", key: "CUSTOMER", width: "5.5%" },
  { label: "GROUP", key: "GROUP", width: "4%" },
  { label: "SUB GROUP", key: "SUB_GROUP", width: "4%" },
  { label: "CATEGORY", key: "CATEGORY", width: "4%" },
  { label: "SUB CAT", key: "SUB_CAT", width: "4%" },
  { label: "ACCOUNT", key: "ACCOUNT", width: "5%" },
  { label: "LOCN", key: "LOCN", width: "5%" },
]; // Total: 4.5*2 + 4 + 6 + 8 + 4*4 + 5*5 + 3 + 5.5*2 = 100%

const PURCHASE_IMPORT_MAP = [
  // Keys must match API exactly (including spaces and case if case-sensitive)
  { label: "CATEGORY", key: "CATEGORY", width: "35%" },
  { label: "TOTAL QTY", key: "TOTAL QTY", width: "10%", isNumeric: true },
  { label: "SALES AMOUNT", key: "SALES AMOUNT", width: "15%", isNumeric: true },
  { label: "COST", key: "COST", width: "10%", isNumeric: true },
  { label: "PROFIT", key: "PROFIT", width: "15%", isNumeric: true },
  { label: "% AGE", key: "%AGE", width: "15%", isNumeric: false },
];

const PURCHASE_ITEMWISE_MAP = [
  // Keys must match API exactly (including spaces)
  { label: "SALESMAN", key: "SALES MAN", width: "15%" }, // Non-numeric, descriptive column
  { label: "GROSS SALE", key: "GROSS SALE", width: "12%", isNumeric: true },
  { label: "DISC", key: "DISC", width: "8%", isNumeric: true },
  { label: "NET SALES", key: "NET SALES", width: "12%", isNumeric: true },
  { label: "COST", key: "COST", width: "10%", isNumeric: true },
  { label: "PROFIT", key: "PROFIT", width: "12%", isNumeric: true },
  { label: "% AGE", key: "%AGE", width: "12%", isNumeric: false }, // Excluded from total sum
  { label: "VAT AMT", key: "VAT AMT", width: "19%", isNumeric: true },
];

const PURCHASERETURNLIST_MAP = [
  { label: "DEPT NO", key: "DEPTNO", width: "15%" }, // Non-numeric, descriptive column
  { label: "GROSS SALE", key: "GROSS SALE", width: "12%", isNumeric: true },
  { label: "DISC", key: "DISC", width: "8%", isNumeric: true },
  { label: "NET SALES", key: "NET SALES", width: "12%", isNumeric: true },
  { label: "COST", key: "COST", width: "10%", isNumeric: true },
  { label: "PROFIT", key: "PROFIT", width: "12%", isNumeric: true },
  { label: "% AGE", key: "%AGE", width: "12%", isNumeric: false }, // Excluded from total sum
  { label: "VAT AMT", key: "VAT AMT", width: "19%", isNumeric: true },
];

const PURCHASERETURN_ITEMWISE_MAP = [
  { label: "DEPT NO", key: "DEPTNO", width: "15%" }, // Non-numeric, descriptive column
  { label: "GROSS SALE", key: "GROSS SALE", width: "12%", isNumeric: true },
  { label: "DISC", key: "DISC", width: "8%", isNumeric: true },
  { label: "NET SALES", key: "NET SALES", width: "12%", isNumeric: true },
  { label: "COST", key: "COST", width: "10%", isNumeric: true },
  { label: "PROFIT", key: "PROFIT", width: "12%", isNumeric: true },
  { label: "% AGE", key: "%AGE", width: "12%", isNumeric: false }, // Excluded from total sum
  { label: "VAT AMT", key: "VAT AMT", width: "19%", isNumeric: true },
];

const PURCHASEREGISTER_MAP = [
  { label: "DEPT NO", key: "DEPTNO", width: "15%" }, // Non-numeric, descriptive column
  { label: "GROSS SALE", key: "GROSS SALE", width: "12%", isNumeric: true },
  { label: "DISC", key: "DISC", width: "8%", isNumeric: true },
  { label: "NET SALES", key: "NET SALES", width: "12%", isNumeric: true },
  { label: "COST", key: "COST", width: "10%", isNumeric: true },
  { label: "PROFIT", key: "PROFIT", width: "12%", isNumeric: true },
  { label: "% AGE", key: "%AGE", width: "12%", isNumeric: false }, // Excluded from total sum
  { label: "VAT AMT", key: "VAT AMT", width: "19%", isNumeric: true },
];

const PURCHASE_MONTH_MAP = [
  { label: "DEPT NO", key: "DEPTNO", width: "15%" }, // Non-numeric, descriptive column
  { label: "GROSS SALE", key: "GROSS SALE", width: "12%", isNumeric: true },
  { label: "DISC", key: "DISC", width: "8%", isNumeric: true },
  { label: "NET SALES", key: "NET SALES", width: "12%", isNumeric: true },
  { label: "COST", key: "COST", width: "10%", isNumeric: true },
  { label: "PROFIT", key: "PROFIT", width: "12%", isNumeric: true },
  { label: "% AGE", key: "%AGE", width: "12%", isNumeric: false }, // Excluded from total sum
  { label: "VAT AMT", key: "VAT AMT", width: "19%", isNumeric: true },
];

const PURCHASE_ITEMWISE_SUMMARY_MAP = [
  { label: "DEPT NO", key: "DEPTNO", width: "15%" }, // Non-numeric, descriptive column
  { label: "GROSS SALE", key: "GROSS SALE", width: "12%", isNumeric: true },
  { label: "DISC", key: "DISC", width: "8%", isNumeric: true },
  { label: "NET SALES", key: "NET SALES", width: "12%", isNumeric: true },
  { label: "COST", key: "COST", width: "10%", isNumeric: true },
  { label: "PROFIT", key: "PROFIT", width: "12%", isNumeric: true },
  { label: "% AGE", key: "%AGE", width: "12%", isNumeric: false }, // Excluded from total sum
  { label: "VAT AMT", key: "VAT AMT", width: "19%", isNumeric: true },
];

const PURCHASE_GROUP_MONTH_MAP = [
  { label: "DEPT NO", key: "DEPTNO", width: "15%" }, // Non-numeric, descriptive column
  { label: "GROSS SALE", key: "GROSS SALE", width: "12%", isNumeric: true },
  { label: "DISC", key: "DISC", width: "8%", isNumeric: true },
  { label: "NET SALES", key: "NET SALES", width: "12%", isNumeric: true },
  { label: "COST", key: "COST", width: "10%", isNumeric: true },
  { label: "PROFIT", key: "PROFIT", width: "12%", isNumeric: true },
  { label: "% AGE", key: "%AGE", width: "12%", isNumeric: false }, // Excluded from total sum
  { label: "VAT AMT", key: "VAT AMT", width: "19%", isNumeric: true },
];

// === COMPONENT HELPERS (Passed the map dynamically) ===

const TableHeader = ({ headerMap }) => (
  <View style={[styles.tableRow, styles.tableHeader]} fixed>
    {headerMap.map((col, index) => (
      <Text
        key={index}
        style={{ ...styles.colDefault, width: col.width, ...styles.colText }}
      >
        {col.label}
      </Text>
    ))}
  </View>
);

const TableBody = ({ rows, headerMap }) =>
  rows.map((row, index) => (
    <View style={styles.tableRow} key={index}>
      {headerMap.map((col, colIndex) => (
        <Text
          key={colIndex}
          style={{
            ...styles.colDefault,
            width: col.width,
            ...(col.isNumeric ? styles.colNum : styles.colText),
          }}
        >
          {col.isNumeric ? formatNumber(row[col.key]) : row[col.key]}
        </Text>
      ))}
    </View>
  ));

const TableFooter = ({ grandColumnTotals, headerMap }) => {
  // Flag to render "GRAND TOTAL:" only once.
  let labelRendered = false;

  return (
    <View style={[styles.tableRow, styles.footerRow]} fixed>
      {/* Iterate over the ENTIRE headerMap to maintain column order */}

      {console.log("headerMap in pdf", headerMap)}
      {headerMap.map((col, index) => {
        // === If it is a Numeric Column (Render Total) ===
        if (col.isNumeric) {
          const totalValue = grandColumnTotals[col.key];
          const content =
            // CRITICAL: Check for undefined/null to correctly display 0 totals (e.g., for COST)
            totalValue !== undefined && totalValue !== null
              ? formatNumber(totalValue)
              : "-"; // Renders '-' if total is missing

          return (
            <Text
              key={index}
              style={{
                ...styles.colDefault,
                width: col.width,
                ...styles.colNum,
                fontWeight: "bold",
              }}
            >
              {content}
            </Text>
          );
        }

        // === If it is a Non-Numeric Column (Render Label or Blank Placeholder) ===
        else {
          // Content is "GRAND TOTAL:" for the first non-numeric cell,
          // and a BLANK STRING ('') for all subsequent non-numeric cells (like %AGE)
          const content = !labelRendered ? "GRAND TOTAL:" : "";

          if (!labelRendered) {
            labelRendered = true;
          }

          return (
            <Text
              key={index}
              style={{
                ...styles.colDefault,
                width: col.width, // Maintains the width of the % AGE column
                ...styles.colText,
                fontWeight: "bold",
              }}
            >
              {content} {/* Renders a blank string for % AGE */}
            </Text>
          );
        }
      })}
    </View>
  );
};

// --- Main PDF Document Component (Uses a dynamic MAP) ---

const VATReportPDF = ({
  rows,
  grandColumnTotals,
  clientDetails,
  currentSelectedModeObject,
}) => {
  console.log("rows VATReportPDF", rows);
  // 🚨 1. SELECT THE CORRECT COLUMN MAP based on the mode
  let HEADER_MAP = [];
  if (currentSelectedModeObject?.value === "PURCHASE_SUPPLIER") {
    HEADER_MAP = PURCHASE_SUPPLIER_MAP;
  } else if (currentSelectedModeObject?.value === "PURCHASELIST") {
    HEADER_MAP = PURCHASELIST_MAP;
  } else if (currentSelectedModeObject?.value === "PURCHASE_LOCAL") {
    HEADER_MAP = PURCHASE_LOCAL_MAP;
  } else if (currentSelectedModeObject?.value === "PURCHASE_IMPORT") {
    HEADER_MAP = PURCHASE_IMPORT_MAP; // because same fields are coming api
  } else if (currentSelectedModeObject?.value === "PURCHASE_ITEMWISE") {
    HEADER_MAP = PURCHASE_ITEMWISE_MAP;
  } else if (currentSelectedModeObject?.value === "PURCHASERETURNLIST") {
    HEADER_MAP = PURCHASERETURNLIST_MAP;
  } else if (currentSelectedModeObject?.value === "PURCHASERETURN_ITEMWISE") {
    HEADER_MAP = PURCHASERETURN_ITEMWISE_MAP;
  } else if (currentSelectedModeObject?.value === "PURCHASEREGISTER") {
    HEADER_MAP = PURCHASEREGISTER_MAP;
  } else if (currentSelectedModeObject?.value === "PURCHASE_MONTH") {
    HEADER_MAP = PURCHASE_MONTH_MAP;
  } else if (currentSelectedModeObject?.value === "PURCHASE_ITEMWISE_SUMMARY") {
    HEADER_MAP = PURCHASE_ITEMWISE_SUMMARY_MAP;
  } else if (currentSelectedModeObject?.value === "PURCHASE_GROUP_MONTH") {
    HEADER_MAP = PURCHASE_GROUP_MONTH_MAP;
  }

  // Add dynamic logic for any unmatched modes. This fixes the VAT Reports where modes like 'ALL', 'INPUT' are not hardcoded.
  if (HEADER_MAP.length === 0 && rows && rows.length > 0) {
    const isNumericHeader = (header) =>
      [
        "AMT",
        "COMMISSION",
        "RETURN",
        "VAT",
        "TOTAL",
        "PROFIT",
        "COST",
        "QTY",
        "AMOUNT",
        "SRNO",
        "VAT_AMT",
        "VATAMT",
        "VAT AMT",
        "TOTAL_AMT",
        "GROSS SALE",
        "DISC",
        "NET SALES",
        "EXCL_VAT_AMOUNT",
      ].some((s) => header.toUpperCase().includes(s));

    const firstRow = rows[0];
    // Identify correct keys to display
    const excludeKeys = [
      "isGroupHeader",
      "isSubHeader",
      "isSubSubHeader",
      "totalAmount",
      "totalAmountExclVAT",
      "id",
      "Rowid",
    ];
    const keys = Object.keys(firstRow).filter((k) => !excludeKeys.includes(k));

    // Distribute width evenly among columns
    let allocatedWidth = 0;
    HEADER_MAP = keys.map((k, index) => {
      const isLast = index === keys.length - 1;
      const widthVal = isLast
        ? 100 - allocatedWidth
        : Math.floor(100 / keys.length);
      allocatedWidth += widthVal;
      return {
        label: k.toUpperCase(),
        key: k,
        width: widthVal + "%",
        isNumeric: isNumericHeader(k),
      };
    });
  }

  // 🚨 2. ADD ROBUST DATA/MAP CHECK
  if (!rows || rows.length === 0 || HEADER_MAP.length === 0) {
    return (
      <Document>
        <Page size="A4" style={styles.page} orientation="landscape">
          <Text style={{ textAlign: "center", marginTop: 50 }}>
            No data available for this report mode......
          </Text>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="landscape">
        {/* Client and Report Header */}
        <View style={styles.clientHeader} fixed>
          <Text style={styles.reportTitle}>
            {currentSelectedModeObject?.label || "VAT Report"}
          </Text>
        </View>

        {/* Main Table */}
        <View style={styles.table}>
          <TableHeader headerMap={HEADER_MAP} />
          <TableBody rows={rows} headerMap={HEADER_MAP} />

          {/* Grand Totals */}
          <TableFooter
            grandColumnTotals={grandColumnTotals}
            headerMap={HEADER_MAP}
          />
        </View>
      </Page>
    </Document>
  );
};

export default VATReportPDF;
